import { IVoicemailConfig } from "./modules/voicemail";
import { IReservationProvider } from "./modules/reservation";
import { IAgentHours, ICustomPrepTimeHours } from "./hours";
import { IPosProvider } from "./pos";
import { HolidayHoursConfig } from "@loman/utils";

export const AgentType = {
  QUESTION: "QUESTION",
  RESERVATION: "RESERVATION",
  PICKUP: "PICKUP",
  PICKUP_DELIVERY: "PICKUP/DELIVERY",
  PICKUP_RESERVATION: "PICKUP/RESERVATION",
  PICKUP_DELIVERY_RESERVATION: "PICKUP/DELIVERY/RESERVATION",
  VOICEMAIL: "VOICEMAIL",
  CATERING: "CATERING",
} as const;
export type AgentType = (typeof AgentType)[keyof typeof AgentType];

export type IAgentSource = "prod" | "local" | "both";

export interface IAgent {
  id: string;
  name: string;
  slug: string;
  type: string;
  formattedAddress?: string | null;
  phoneNumber: string;
  enabled: boolean;
  pausedUntil: string | null;
  panic: boolean;
  source: IAgentSource;
  updatedAt: string | null;
  startedAt: string | null;
  onboardingStartedAt: string | null;
  menuUpdatedAt: string | null;
  stripeSubscriptionId: string | null;
}

export interface IFaq {
  id?: string;
  question: string;
  answer: string;
}

export interface IMessage {
  key: string;
  value: string;
}
export interface ITransfer {
  key: string;
  value: string | string[];
}

export interface ITwilioOverride {
  enabled: boolean;
  sid?: string;
  auth?: string;
}

export const BillingStyle = {
  FLAT: "FLAT",
  METERED: "METERED",
} as const;
export type BillingStyle = (typeof BillingStyle)[keyof typeof BillingStyle];

export const Pos = {
  TOAST: "toast",
  TOAST_LEGACY: "toast-legacy",
  SQUARE: "square",
  CLOVER: "clover",
  SKYTAB: "skytab",
  STREAM: "stream",
  KWICK: "kwick",
  LOMAN: "loman",
  SPOT_ON: "spoton",
  NCR_ALOHA_ESSENTIALS: "ncr_aloha_essentials",
  FOODTEC: "foodtec",
} as const;
export type Pos = (typeof Pos)[keyof typeof Pos];

export type ISO8601DateString = string;

export interface IAddress {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface IUser {
  id: string;
  email: string;
  isAdmin: boolean;
}

export interface IAgentBlueprint {
  accountId?: string;
  address?: string;
  addressComponents?: IAddress;
  allowAnon?: boolean;
  billingStyle?: BillingStyle | null;
  createdAt?: ISO8601DateString;
  deletedAt?: ISO8601DateString | null;
  disallowClosedTransfers?: boolean;
  enabled?: boolean;
  fallbackPhoneNumber?: string | null;
  faqs?: IFaq[] | null;
  greetingMessageOpen: string;
  greetingMessageOrderingClosed: string;
  greetingMessageClosed: string;
  hours: IAgentHours | null;
  hoursOrdering?: IAgentHours | null;
  hoursLoman?: IAgentHours | null;
  hubspotDealId?: string;
  id?: string;
  messages?: IMessage[];
  name: string;
  panic?: boolean;
  pausedUntil?: ISO8601DateString | null;
  phoneNumber?: string | null;
  placeId: string;
  posExternalId?: string | null;
  reservationExternalId?: string | null;
  pos?: Pos | null;
  posProvider: IPosProvider | null;
  prompt?: string | null;
  reservationProvider?: IReservationProvider | null;
  slug?: string;
  smsPhoneNumber?: string | null;
  startedAt?: ISO8601DateString;
  onboardingStartedAt?: ISO8601DateString;
  streamConnectedAt?: ISO8601DateString | null;
  stripeAccountId?: string | null;
  stripeChargesEnabled?: boolean;
  stripeCustomerId?: string | null;
  stripeSubscriptionId?: string | null;
  timezone: string | null;
  transfers?: ITransfer[];
  twilioOverride?: ITwilioOverride | null;
  twoPartyConsent: boolean | null;
  type: AgentType;
  updatedAt?: ISO8601DateString;
  users?: IUser[] | null;
  voicemailConfig?: IVoicemailConfig | null;
  // Ordering prep times stored at root (separate from orderingModuleConfig.prepTime)
  pickupPrepTime?: string | null; // minutes as string for backend compatibility
  deliveryPrepTime?: string | null; // minutes as string for backend compatibility
  customPrepTimeEnabled?: boolean;
  customPrepTimeHours?: ICustomPrepTimeHours; // hours object defining custom prep time windows with per-mode durations
  holidayHours?: HolidayHoursConfig | null;
}
